import { useRef, useState } from 'react';
import { BITRATE_OPTIONS } from '../lib/api';

export type UploadProgress = { id: string; filename: string; fraction: number; error?: string };

type Props = {
  disabled: boolean;
  uploads: UploadProgress[];
  bitrate: number;
  onBitrateChange: (kbps: number) => void;
  onFiles: (files: File[]) => void;
};

const AUDIO_PATTERN = /\.(wav|aiff?|flac|mp3|m4a|aac|ogg|opus)$/i;

const BITRATE_LABEL: Record<number, string> = { 64: 'Standard', 96: 'Higher', 128: 'Highest' };

export function UploadZone({ disabled, uploads, bitrate, onBitrateChange, onFiles }: Props) {
  const [hot, setHot] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const accept = (list: FileList | null) => {
    if (!list) return;
    const files = Array.from(list).filter((f) => AUDIO_PATTERN.test(f.name));
    if (files.length) onFiles(files);
  };

  return (
    <>
      {/* 放在拖拽区外面：它在里面的话点一下就会弹出文件选择框 */}
      <div className="bitrate-bar">
        <span className="bitrate-label">Bitrate</span>
        <div className="segmented">
          {BITRATE_OPTIONS.map((kbps) => (
            <button
              key={kbps}
              className={kbps === bitrate ? 'on' : ''}
              onClick={() => onBitrateChange(kbps)}
            >
              {kbps}k <em>{BITRATE_LABEL[kbps]}</em>
            </button>
          ))}
        </div>
        <small>Set per album — used for new uploads and re-encodes</small>
      </div>

      <div
        className={`dropzone ${hot && !disabled ? 'hot' : ''}`}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setHot(true); }}
        onDragLeave={() => setHot(false)}
        onDrop={(e) => {
          e.preventDefault();
          setHot(false);
          if (!disabled) accept(e.dataTransfer.files);
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        style={{ cursor: disabled ? 'default' : 'pointer' }}
      >
        <strong>Drop master files here</strong>
        <small>
          wav / aiff / flac / mp3 / m4a. Each one is normalized to -16 LUFS first,
          then encoded to {bitrate}k xHE-AAC plus a 128k AAC-LC fallback.
        </small>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="audio/*"
          hidden
          onChange={(e) => { accept(e.target.files); e.target.value = ''; }}
        />
      </div>

      {uploads.map((item) => (
        <div className="upload-row" key={item.id}>
          <span className="upload-name">{item.filename}</span>
          {item.error ? (
            <span className="upload-err">{item.error}</span>
          ) : (
            <>
              <div className="progress">
                <div style={{ width: `${Math.round(item.fraction * 100)}%` }} />
              </div>
              <span className="upload-pct">{Math.round(item.fraction * 100)}%</span>
            </>
          )}
        </div>
      ))}
    </>
  );
}
